import type { Contract, ContractCost } from "../lib/types";
import { eur } from "../lib/format";

interface Props {
  contract: Contract;
  cost: ContractCost;
  isLowest: boolean;
  onEdit: (c: Contract) => void;
}

export function ContractCard({ contract, cost, isLowest, onEdit }: Props) {
  const variabel = cost.total_eur - cost.breakdown.vastrecht_eur;
  return (
    <div
      className={`card card-hover ${isLowest ? "border-2 !border-green" : ""}`}
      onClick={() => onEdit(contract)}
    >
      <div className="flex justify-between items-start mb-1">
        <div>
          <div className="flex gap-2 items-center flex-wrap">
            <h3 className="m-0 text-base font-semibold">{contract.name}</h3>
            <span className={`tag ${contract.type === "dynamisch" ? "tag-terra" : "tag-line"}`}>
              {contract.type}
            </span>
            {isLowest && <span className="tag tag-green">goedkoopst</span>}
          </div>
          {contract.notes && <div className="help mt-1.5">{contract.notes}</div>}
        </div>
      </div>

      <hr className="divider" />

      <div className="flex justify-between items-baseline">
        <div className="flex flex-col">
          <span className="label-up">
            Jaarkosten {cost.curtailment_applied ? "(incl. curtailment)" : ""}
          </span>
          {cost.curtailment_applied && (
            <span className="text-[11px] text-ink-subtle mt-0.5">
              zonder curtailment: {eur(cost.total_eur + cost.curtailment_savings_eur, 0)}
            </span>
          )}
        </div>
        <span className={`bignum ${isLowest ? "text-green" : "text-ink"}`}>
          {eur(cost.total_eur, 0)}
        </span>
      </div>

      <div className="mt-3 text-xs text-ink-muted flex gap-3 flex-wrap">
        <span>
          Vast: <span className="mono text-ink">{eur(cost.breakdown.vastrecht_eur)}</span>
        </span>
        <span>
          Variabel: <span className="mono text-ink">{eur(variabel)}</span>
        </span>
        {cost.breakdown.terugleverkosten_eur > 0 && (
          <span>
            Terugleverkn:{" "}
            <span className="mono text-rust">{eur(cost.breakdown.terugleverkosten_eur)}</span>
          </span>
        )}
      </div>
      {cost.curtailment_break_even_eur_per_kwh !== null && (
        <div className="mt-2 text-xs flex items-center gap-2 flex-wrap">
          <span className="tag tag-terra">curtailment break‑even</span>
          <span className="mono text-ink">
            {cost.curtailment_break_even_eur_per_kwh < 0 ? "−" : ""}€
            {Math.abs(cost.curtailment_break_even_eur_per_kwh).toFixed(4)}/kWh
          </span>
          {cost.curtailment_applied && (
            <span className="text-ink-muted">
              · {cost.curtailment_hours}u/jaar, bespaart {eur(cost.curtailment_savings_eur)}
            </span>
          )}
        </div>
      )}
      {cost.vast_export_value_eur_per_kwh !== null && (
        <div className="mt-2 text-xs flex items-center gap-2 flex-wrap">
          <span className={`tag ${cost.vast_export_value_eur_per_kwh >= 0 ? "tag-green" : "tag-warn"}`}>
            export waarde per kWh
          </span>
          <span className="mono text-ink">
            {cost.vast_export_value_eur_per_kwh < 0 ? "−" : "+"}€
            {Math.abs(cost.vast_export_value_eur_per_kwh).toFixed(4)}/kWh
          </span>
          <span className="text-ink-muted">
            {cost.vast_export_value_eur_per_kwh >= 0
              ? "(curtailment niet voordelig)"
              : "(curtailment loont)"}
          </span>
        </div>
      )}
    </div>
  );
}
